import { CORE_API } from "../constants";
import { TTranslations } from "../state/atoms/translations.atom";
import { getData } from "./common.service";

interface LocalData{
  lang:string;
  timestamp:number;
  translations: TTranslations
}

class LocaleService {
  private static readonly baseUrl = "/locale-list";
  private static locales: TTranslations = {};
  private static readonly timeValidationOfStorage = 600 * 1000; // Cache expiration time (in milliseconds)


  /**
   * Initialize IndexedDB and return the database instance.
   * Ensures the object store for locales is created.
   */
  private static async getDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open("LocaleDB", 1);

      // Create object store if needed
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains("locales")) {
          const store = db.createObjectStore("locales", { keyPath: "lang" });
          store.createIndex("timestamp", "timestamp", { unique: false });
        }
      };

      // Resolve the database instance on success
      request.onsuccess = (event) => resolve((event.target as IDBOpenDBRequest).result);

      // Reject on error
      request.onerror = (event) => reject((event.target as IDBOpenDBRequest).error);
    });
  }

  /**
   * Retrieve cached locales from IndexedDB for a given language.
   * @param lang The language for which cached locales are retrieved.
   * @returns Cached locale data or null if not available.
   */
  private static async getCachedLocales(lang: string): Promise<LocalData> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction("locales", "readonly");
      const store = transaction.objectStore("locales");
      const request = store.get(lang);

      // Resolve with data if found
      request.onsuccess = () => resolve(request.result || null);

      // Reject on error
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save fetched locales to IndexedDB.
   * @param lang The language key for the locales.
   * @param translations The translations to be saved.
   */
  private static async saveLocalesToDb(lang: string, translations: TTranslations): Promise<void> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction("locales", "readwrite");
      const store = transaction.objectStore("locales");
      const data = { lang, translations, timestamp: Date.now() }; // Add a timestamp for cache validation

      const request = store.put(data);

      // Resolve on success
      request.onsuccess = () => resolve();

      // Reject on error
      request.onerror = () => reject(request.error);
    });
  }


  /**
   * Fetch locales for a given language.
   * Checks IndexedDB for cached data before making an API call.
   * Caches fetched data in IndexedDB with an expiration policy.
   * @param lang The language to fetch locales for. Defaults to "en".
   * @returns The fetched or cached translations.
   */
  static async getLocales(lang: TLang = "en"): Promise<TTranslations | null> {
    const now = Date.now();
  
    // Check IndexedDB for cached data
    const cachedData = await this.getCachedLocales(lang);
    if (cachedData && now - cachedData.timestamp < this.timeValidationOfStorage) {
      // console.log("Using cached locales from IndexedDB");
      this.locales = cachedData.translations;
      return this.locales;
    }
  
    // console.log("Fetching locales from API");
  
    // Prepare API URL
    let url = `${LocaleService.baseUrl}`;
    if (lang !== "all") {
      const filterQuery = { fields: { id: true, [lang]: true } };
      url += `?filter=${JSON.stringify(filterQuery)}`;
    }
  
    // Fetch from API
    const localesResponse: TTranslationsResponse = await getData(url, {
      sendTokenInHeader: true,
      basePath: CORE_API,
    });
  
    // Handle API errors
    if ("error" in localesResponse) {
      console.error("Error fetching locales:", localesResponse);
      return null;
    }
  
    // Process and store translations
    const translations: TTranslations = {};
    localesResponse.forEach((translation: TTranslation) => {
      translations[translation.id] = translation[lang];
    });
  
    this.locales = translations;
  
    // Save translations to IndexedDB
    await this.saveLocalesToDb(lang, translations);
  
    return translations;
  }

  static translate(key: string, translationVariables: TTranslationVariabels = {}) {
    const translationHasKey = Boolean(LocaleService.locales[key]);
    const translationHasVariables = Object.keys(translationVariables).length > 0;

    if(!translationHasKey) {
      return `Translation Missing: ${key}`;
    }

    if(translationHasVariables) {
      return replaceVariables(LocaleService.locales[key], translationVariables);
    }

    return LocaleService.locales[key];
  }
}

export const t = (text: string, translationVariables: TTranslationVariabels = {}) => {
  return LocaleService.translate(text, translationVariables);
};

export default LocaleService;

/**
 * ================== TYPES ======================
 */
export type TLang = "en" | "hi" | "gu" | "ka" | "ma" | "te" | "all";
type TTranslationVariabels = {
  [key: string]: string;
}

type TTranslation = { id: string, [key: string]: string };
type TTranslationsSuccess = Array<TTranslation>;
type TTranslationsError = { error: { name: string } };
type TTranslationsResponse = TTranslationsSuccess | TTranslationsError;


export type TLocale = {
  id: string;
  en?: string,
  gu?: string,
  hi?: string,
  te?: string,
  ka?: string,
  ma?: string,
  all?: string
};

export type TAppNotifs = Array<TLocale>;


/**
 * ================== Helpers ======================
 */

const replaceVariables = (template: string, variables: TTranslationVariabels): string => {
  const variablePattern = /{([^}]+)}/g;

  const replacedString = template.replace(variablePattern, (match, variable) => {
    const trimmedVariable = variable.trim();
    return variables.hasOwnProperty(trimmedVariable) ? variables[trimmedVariable] : match;
  });

  return replacedString;
};
